/**
 * Table cell-edit routing (S17/P2b) — the pure half of `ViewTableContainer`'s
 * write path. A committed getter-cell edit becomes ONE typed model op for the
 * WYSIWYG mutation service (`mutateModule` → engine `setFieldCellEdit` /
 * setAspect synthesis): `@field` columns write the field, `@@aspect` columns
 * write the aspect, on the ROW's element (its `#id` when it carries one, else
 * its qualified key). Read-only columns (calc, builtin, capability — the
 * engine's `computeTableData` editability annotation) route to `null`: never
 * a write.
 */
import { renderValueLiteral, type ModuleMutation, type TableData } from "@archlang/engine";

/** The mutation a committed cell edit maps to, or `null` when the column is
 *  read-only / the row is unknown (a refused edit, not a throw). */
export function cellWrite(
  data: TableData,
  rowKey: string,
  columnIndex: number,
  rawValue: string,
): { readonly targetId: string; readonly op: ModuleMutation } | null {
  const col = data.columns[columnIndex];
  if (!col?.editable || !col.writeSigil || !col.writePath) return null;
  const row = data.rows.find((r) => r.key === rowKey);
  if (!row) return null;
  const targetId = row.id ? `#${row.id}` : row.key;
  const path = col.writePath;
  const value = renderValueLiteral(rawValue);
  const op: ModuleMutation =
    col.writeSigil === "@@"
      ? { op: "setAspect", path, value }
      : { op: "setField", path, value };
  return { targetId, op };
}
